import { Alert, Text } from '@common/ui'
import { com } from '@eidu/web'
import useFormContent from '../../../../io/useFormContent'
import FormPayloadEditor from './FormPayloadEditor'
import encodeFormContentToYaml = com.eidu.sharedlib.form.encodeFormContentToYaml

type FormServiceFallbackProps = {
  formId: string
  formContentId: string
}

// Legacy read path for forms that predate the asset.resource link (not yet migrated). Encodes the form-service pages
// to YAML so the Source tab offers the same Visual/YAML toggle as a migrated form.
const FormServiceFallback = ({ formId, formContentId }: FormServiceFallbackProps) => {
  const { formContent, error } = useFormContent({ formId, formContentId })
  const errorMessage = error instanceof Error ? error.message : undefined

  if (errorMessage) return <Alert severity="negative">{errorMessage}</Alert>
  if (!formContent) return null
  if (!formContent.asJsReadonlyArrayView().every((page) => page.key)) {
    return (
      <Alert severity="negative">
        Some pages on this form do not have a key set. This is not supported. Contact EIDU if the issue persists.
      </Alert>
    )
  }
  return <FormPayloadEditor yaml={encodeFormContentToYaml(formContent)} editable={false} />
}

type FormSourceViewProps = {
  content: string | undefined
  isLoading: boolean
  formId: string
  formContentId: string | undefined
}

/**
 * Renders the read-only Form payload in the asset view's Source tab with the Visual/YAML toggle. Prefers the
 * asset.resource YAML, falling back to form-service content for forms without an asset.resource link yet.
 */
const FormSourceView = ({ content, isLoading, formId, formContentId }: FormSourceViewProps) => {
  if (isLoading) {
    return (
      <Text size="sm" c="dimmed">
        Loading...
      </Text>
    )
  }

  if (content !== undefined) return <FormPayloadEditor yaml={content} editable={false} />

  if (formContentId) return <FormServiceFallback formId={formId} formContentId={formContentId} />
  return null
}

export default FormSourceView
